import { startBandwidthBudget } from './shakaLoader';

// Past 2x a phone screen shows no more detail, only more bytes.
const MAX_DPR = 2;
// A rung 10% short of the element is not visibly softer.
const COVER_SLACK = 0.9;
/**
 * With no bandwidth estimate, the first rung stops at this short side (540p):
 * sharp on a card, light on an unknown network, like hls.js's low default.
 */
export const UNKNOWN_BANDWIDTH_START_SIDE = 540;

export type StartSurface = 'feed' | 'card';
// Last laid-out size per surface, so a warm that runs before its element
// exists aims where the player will measure.
const surfaceSides = new Map<StartSurface, number>();

/** What the first rung has to serve; shared by the warm chain and the player. */
export interface StartTarget {
	/** Device pixels the element's short side needs. */
	shortSidePx: number;
	/** Bits per second the first fragment may spend; null when unknown. */
	budgetBps: number | null;
}

/**
 * Target for an element whose short side is `cssShortSide` CSS pixels; the
 * viewport (the shorts feed) when the size is unknown. Null on the server.
 */
export function startTarget(cssShortSide?: number): StartTarget | null {
	if (typeof window === 'undefined') return null;
	const css =
		cssShortSide && cssShortSide > 0
			? cssShortSide
			: Math.min(window.innerWidth, window.innerHeight);
	return {
		shortSidePx: Math.round(
			css * Math.min(window.devicePixelRatio || 1, MAX_DPR)
		),
		budgetBps: startBandwidthBudget()
	};
}

/** Records the laid-out size of a playing element of this surface. */
export function rememberSurfaceSide(
	surface: StartSurface,
	element: HTMLElement | null | undefined
): void {
	const side = elementShortSide(element);
	if (side) surfaceSides.set(surface, side);
}

/** CSS short side last measured on this surface, if any. */
export function surfaceSide(surface: StartSurface): number | undefined {
	return surfaceSides.get(surface);
}

/** Short side of an element in CSS pixels; undefined while it has no layout. */
export function elementShortSide(
	element: HTMLElement | null | undefined
): number | undefined {
	if (!element) return undefined;
	const side = Math.min(element.clientWidth, element.clientHeight);
	return side > 0 ? side : undefined;
}

export function levelShortSide(level: {
	width?: number;
	height?: number;
}): number {
	const { width, height } = level;
	if (width && height) return Math.min(width, height);
	return height ?? width ?? 0;
}

export function coversTarget(
	level: { width?: number; height?: number },
	target: StartTarget
): boolean {
	return levelShortSide(level) >= target.shortSidePx * COVER_SLACK;
}
